import { Button, Input, NumberInput, Select } from "@mantine/core";
import {
  IconBuildingWarehouse,
  IconCar,
  IconFileText,
  IconLayoutGrid,
  IconMapPin,
  IconPackage,
  IconStack,
  IconTruck,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { orpcClient } from "@/lib/orpc-client";
import { single, allOrThrow } from "@/lib/orpc-batch";
import { useData } from "@/utility/state/contextAPI";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

function EditInventoryCreatedListPage() {
  const { t } = useTranslation();
  const [inventoryCreatedData, setInventoryCreatedData] = useState({
    company: localStorage.getItem("companyId"),
    name: "",
    remarks: "",
    productList: [],
    deliveryPerson: "",
    licensePlateNumber: "",
    warehouseStaff: "18",
  });
  const [editInventoryCreatedData, setEditInventoryCreatedData] = useState({});
  const [tempProductList, setTempProductList] = useState([]);
  const [tempProductData, setTempProductData] = useState([]);
  const [tempProductInput, setTempProductInput] = useState([]);
  const [tempWarehouseData, setTempWarehouseData] = useState([]);
  const [tempWarehouseInput, setTempWarehouseInput] = useState([]);
  const [tempAreaInput, setTempAreaInput] = useState([]);
  const [tempStorageLocationInput, setTempStorageLocationInput] = useState([]);
  const [tempAmount, setTempAmount] = useState([]);
  const [disable, setDisable] = useState([]);
  const [warehouseMap, setWarehouseMap] = useState({});
  const [employeeData, setEmployeeData] = useState([]);
  const [warehouseStaffInput, setWarehouseStaffInput] = useState("");

  const [deleteProductList, setDeleteProductList] = useState([]);
  const [productError, setProductError] = useState([]);
  const [storageLocationError, setStorageLocationError] = useState([]);

  const { id } = useParams();

  const {
    modal: { contextOpenModal, contextCloseModal, contextSetModalContent },
  } = useData();
  const navigate = useNavigate();

  const handleSubmit = async (
    editInventoryCreatedData,
    tempProductList,
    deleteProductList
  ) => {
    try {
      if (Object.keys(editInventoryCreatedData)?.length) {
        await single(
          orpcClient.stockReceipt.update([
            {
              id: id!,
              ...editInventoryCreatedData,
            },
          ])
        );
      }

      if (deleteProductList?.length) {
        await allOrThrow(
          orpcClient.stockReceiptProduct.delete(
            deleteProductList.map((productId) => ({ id: productId }))
          )
        );
      }

      let editTempProductList = [];

      let newTempProductList = [];

      tempProductList?.map((t) => {
        if (t?.id) {
          editTempProductList.push(t);
        } else {
          newTempProductList.push(t);
        }
      });

      if (editTempProductList?.length) {
        await allOrThrow(
          orpcClient.stockReceiptProduct.update(
            editTempProductList.map((data) => ({
              id: data?.id,
              stockReceiptId: id,
              amount: data?.amount,
            }))
          )
        );
      }

      if (newTempProductList?.length) {
        await allOrThrow(
          orpcClient.stockReceiptProduct.create(
            newTempProductList.map((data) => ({
              amount: data?.amount,
              productId: data?.product?.id,
              stockReceiptId: id!,
              storageLocationId: data?.storageLocation,
            }))
          )
        );
      }
    } catch (error) {
      console.error("api error", error);
      return;
    }
    navigate(`/hrMainPage/inventoryCreated/${id}`);
  };

  const handleDataChange = (name, value) => {
    setInventoryCreatedData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setEditInventoryCreatedData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const parseId = (value) =>
    typeof value === "string"
      ? value.match(/ID:\s*([a-zA-Z0-9-]+)/)?.[1] ?? null
      : null;

  const isProductInputValid = (value) => {
    const productId = parseId(value);
    return !!productId && tempProductData.some((p) => p?.id == productId);
  };

  const isStorageLocationInputValid = (value) => {
    const locationId = parseId(value);
    return (
      !!locationId &&
      tempWarehouseData.some((w) =>
        (w?.storageLocation ?? []).some((l) => l?.id == locationId)
      )
    );
  };

  const validateProductInput = (index) => {
    setProductError((prev) => {
      const updated = [...prev];
      const value = tempProductInput[index];
      updated[index] = !!value && !isProductInputValid(value);
      return updated;
    });
  };

  const validateStorageLocationInput = (index) => {
    setStorageLocationError((prev) => {
      const updated = [...prev];
      const value = tempStorageLocationInput[index];
      updated[index] = !!value && !isStorageLocationInputValid(value);
      return updated;
    });
  };

  const handleChangeWarehouseStaff = (value, employeeData) => {
    setWarehouseStaffInput(value);
    const idNumber = parseId(value);
    const selectedData = employeeData.find((e) => e?.id == idNumber);
    handleDataChange("warehouseStaffId", selectedData?.id ?? null);
  };

  const handleDeleteProductList = (index, id) => {
    const updatedProductList = [...tempProductList];

    updatedProductList.splice(index, 1);

    setTempProductInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempWarehouseInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempAreaInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempStorageLocationInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempAmount((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setDisable((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setProductError((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setStorageLocationError((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempProductList(updatedProductList);

    //新增的不用call delete API
    if (id) {
      setDeleteProductList((prev) => [...prev, id]);
    }
  };

  const handleOnOptionSubmit = (type, e, data, index) => {
    let selectedData;
    if (type === "product") {
      let match = e.match(/ID:\s*([a-zA-Z0-9-]+)/);
      let idNumber = match ? match[1] : null;
      selectedData = data.filter((e) => e?.id == idNumber)[0];
      setTempProductList((prev) => {
        const updatedList = [...prev];
        updatedList[index] = { ...updatedList[index], [type]: selectedData };
        return updatedList;
      });
      setProductError((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });
    } else {
      let idBehindMatch = e.match(/ID:\s*([a-zA-Z0-9-]+)/);
      let documentID = idBehindMatch ? idBehindMatch[1].trim() : null;

      setTempProductList((prev) => {
        const updatedList = [...prev];
        updatedList[index] = {
          ...updatedList[index],
          storageLocation: documentID,
        };
        return updatedList;
      });
      setStorageLocationError((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });
    }
  };

  const handleAddProductList = (productList) => {
    const updatedProductList = [...productList, []];

    setTempProductList(updatedProductList);
    setDisable((prev) => {
      return [...prev, false];
    });
  };

  const handleDisplaySelectMembers = (data) => {
    return data?.map((t) => ({
      value: t?.id,
      label: t?.name,
    }));
  };
  const handleDisplaySelectWarehouse = (data) => {
    return data?.map((t) => ({ value: t?.id, label: t?.name }));
  };

  const handleDisplaySelectArea = (warehouseId, warehouseData) => {
    if (!warehouseId) return [];

    const selectedData = warehouseData.find((e) => e?.id == warehouseId);

    return (selectedData?.areas ?? []).map((area) => ({
      value: area?.id,
      label: area?.name,
    }));
  };

  const handleDisplaySelectStorageLocation = (
    warehouseId,
    areaId,
    warehouseData
  ) => {
    if (!warehouseId || !areaId) return [];

    const selectedWarehouse = warehouseData.find((e) => e?.id == warehouseId);
    const selectedArea = (selectedWarehouse?.areas ?? []).find(
      (a) => a?.id == areaId
    );

    return (selectedArea?.storageLocation ?? []).map((location) => ({
      value: location?.id,
      label: location?.name,
    }));
  };

  const {
    // company,
    name,
    remarks,
    deliveryPerson,
    licensePlateNumber,
    warehouseStaff,
    // contact,
    // storage_locations,
  } = inventoryCreatedData || {};
  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await single(orpcClient.stockReceipt.read([{ id: id! }]));
        if (!result) return;
        const productList = await orpcClient.stockReceiptProduct.list({
          filter: { stockReceiptId: id },
        });

        setInventoryCreatedData(result);

        setWarehouseStaffInput(result?.warehouseStaffId ?? "");

        const warehouses = await orpcClient.warehouse.list({});
        const warehouseDataMap = {};
        await Promise.all(
          (warehouses ?? []).map(async (warehouse) => {
            const areas = await orpcClient.warehouseArea.list({
              filter: { warehouseId: warehouse?.id },
            });
            (areas ?? []).forEach((area) => {
              if (area?.id) warehouseDataMap[area.id] = warehouse;
            });
          })
        );
        setWarehouseMap(warehouseDataMap);

        setTempProductList(
          result?.stockReceiptProduct.map((item) => {
            const {
              id,
              amount,
              createdAt,
              updatedAt,
              stockReceiptId,
              productId,
              storageLocationId,
            } = item;

            return {
              id,
              amount,
              createdAt,
              updatedAt,
              stockReceiptId,
              productId,
              storageLocationId,
            };
          })
        );

        setTempProductInput(
          result?.stockReceiptProduct?.map((item) => item?.productId)
        );
        setTempWarehouseInput(
          result?.stockReceiptProduct?.map((item) => {
            const warehouseAreaId = item?.storageLocation?.warehouseAreaId;
            const warehouse = warehouseDataMap[warehouseAreaId];
            return warehouse ? warehouse?.id : null;
          })
        );
        setTempAreaInput(
          result?.stockReceiptProduct?.map(
            (item) => item?.storageLocation?.warehouseAreaId ?? null
          )
        );
        setTempStorageLocationInput(
          result?.stockReceiptProduct?.map(
            (item) => item?.storageLocationId
          )
        );

        setTempAmount(result?.stockReceiptProduct?.map((item) => item?.amount));

        setDisable(result?.stockReceiptProduct?.map(() => true));
      } catch (error) {
        console.error("Error fetching stock receipt data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.employee.list({});
        setEmployeeData(result);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.product.list({});

        setTempProductData(result);
      } catch (error) {
        console.error("Error fetching products data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.warehouse.list({});

        const warehousesWithLocations = await Promise.all(
          (result ?? []).map(async (warehouse) => {
            const areas = await orpcClient.warehouseArea.list({
              filter: { warehouseId: warehouse?.id },
            });
            const storageLocation = (areas ?? []).flatMap(
              (area) => area?.storageLocation ?? []
            );
            return { ...warehouse, areas: areas ?? [], storageLocation };
          })
        );

        setTempWarehouseData(warehousesWithLocations);
      } catch (error) {
        console.error("Error fetching warehouses data:", error);
      }
    };

    fetchData();
  }, []);

  const fieldWrapper = (
    label: React.ReactNode,
    Icon: React.ComponentType<{ size?: number | string }> | undefined,
    children: React.ReactNode
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 space-y-5">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("warehouse.editStockReceipt") || "編輯入庫單"}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {name || t("warehouse.stockReceiptManagement") || "入庫單管理"}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.basicInfo") || "基本資訊"}
            </h3>
          </div>
          <div className="p-6">
            {fieldWrapper(
              t("warehouse.stockReceiptName") || "入庫單名稱",
              IconPackage,
              <Input
                value={name}
                onChange={({ target: { value } }) =>
                  handleDataChange("name", value)
                }
                leftSection={<IconPackage size={16} />}
              />
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconPackage size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.productList") || "商品清單"}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            {tempProductList.map((item, i) => {
              return (
                <div
                  className="rounded-lg border border-gray-200 p-4"
                  key={item?.id ?? i}
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">
                      #{i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDeleteProductList(i, item?.id)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-red-500 hover:bg-red-50"
                    >
                      <IconX size={16} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {fieldWrapper(
                      t("warehouse.productName") || "商品名稱",
                      IconPackage,
                      <Select
                        searchable
                        nothingFoundMessage="查無資料"
                        disabled={disable[i]}
                        leftSection={<IconPackage size={16} />}
                        data={handleDisplaySelectMembers(tempProductData)}
                        value={tempProductInput[i] || null}
                        onChange={(value) => {
                          setTempProductInput((prev) => {
                            const updated = [...prev];
                            updated[i] = value;
                            return updated;
                          });
                          const selectedData = tempProductData.find(
                            (p) => p?.id == value
                          );
                          setTempProductList((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i],
                              product: selectedData,
                            };
                            return updated;
                          });
                        }}
                      />
                    )}

                    {fieldWrapper(
                      t("warehouse.warehouseLabel") || "倉庫",
                      IconBuildingWarehouse,
                      <Select
                        searchable
                        nothingFoundMessage="查無資料"
                        disabled={disable[i]}
                        leftSection={<IconBuildingWarehouse size={16} />}
                        data={handleDisplaySelectWarehouse(tempWarehouseData)}
                        value={tempWarehouseInput[i] || null}
                        onChange={(value) => {
                          setTempWarehouseInput((prev) => {
                            const updated = [...prev];
                            updated[i] = value;
                            return updated;
                          });

                          setTempAreaInput((prev) => {
                            const updated = [...prev];
                            updated[i] = null;
                            return updated;
                          });
                          setTempStorageLocationInput((prev) => {
                            const updated = [...prev];
                            updated[i] = null;
                            return updated;
                          });
                          setTempProductList((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i],
                              storageLocation: null,
                            };
                            return updated;
                          });
                        }}
                      />
                    )}

                    {fieldWrapper(
                      t("warehouse.areaLabel") || "區",
                      IconLayoutGrid,
                      <Select
                        searchable
                        nothingFoundMessage="查無資料"
                        disabled={disable[i]}
                        leftSection={<IconLayoutGrid size={16} />}
                        data={handleDisplaySelectArea(
                          tempWarehouseInput[i],
                          tempWarehouseData
                        )}
                        value={tempAreaInput[i] || null}
                        onChange={(value) => {
                          setTempAreaInput((prev) => {
                            const updated = [...prev];
                            updated[i] = value;
                            return updated;
                          });

                          setTempStorageLocationInput((prev) => {
                            const updated = [...prev];
                            updated[i] = null;
                            return updated;
                          });
                          setTempProductList((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i],
                              storageLocation: null,
                            };
                            return updated;
                          });
                        }}
                      />
                    )}

                    {fieldWrapper(
                      t("warehouse.storageLocationLabel") || "儲位",
                      IconMapPin,
                      <Select
                        searchable
                        nothingFoundMessage="查無資料"
                        disabled={disable[i]}
                        leftSection={<IconMapPin size={16} />}
                        data={handleDisplaySelectStorageLocation(
                          tempWarehouseInput[i],
                          tempAreaInput[i],
                          tempWarehouseData
                        )}
                        value={tempStorageLocationInput[i] || null}
                        onChange={(value) => {
                          setTempStorageLocationInput((prev) => {
                            const updated = [...prev];
                            updated[i] = value;
                            return updated;
                          });
                          setTempProductList((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i],
                              storageLocation: value,
                            };
                            return updated;
                          });
                        }}
                      />
                    )}

                    {fieldWrapper(
                      t("warehouse.quantity") || "數量",
                      IconStack,
                      <NumberInput
                        leftSection={<IconStack size={16} />}
                        value={tempAmount[i] || ""}
                        onChange={(e) => {
                          setTempProductList((prev) => {
                            const updatedList = [...prev];
                            updatedList[i] = { ...updatedList[i], amount: e };
                            return updatedList;
                          });

                          setTempAmount((prev) => {
                            const updated = [...prev];
                            updated[i] = e;
                            return updated;
                          });
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}

            <Button
              variant="default"
              fullWidth
              radius="md"
              onClick={() => handleAddProductList(tempProductList)}
            >
              {t("warehouse.addProductRow") || "增加商品列"}
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconTruck size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.deliveryInfo") || "交貨資訊"}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {fieldWrapper(
                t("warehouse.deliveryPerson") || "送貨人員",
                IconTruck,
                <Input
                  leftSection={<IconTruck size={16} />}
                  value={deliveryPerson}
                  onChange={(e) =>
                    handleDataChange("deliveryPerson", e.target.value)
                  }
                />
              )}
              {fieldWrapper(
                t("warehouse.licensePlateNumber") || "送貨車牌",
                IconCar,
                <Input
                  leftSection={<IconCar size={16} />}
                  value={licensePlateNumber ?? ""}
                  onChange={(e) =>
                    handleDataChange("licensePlateNumber", e.target.value)
                  }
                />
              )}
              {fieldWrapper(
                t("warehouse.inboundStaff") || "入庫人員",
                IconUser,
                <Select
                  searchable
                  clearable
                  nothingFoundMessage="查無資料"
                  leftSection={<IconUser size={16} />}
                  value={warehouseStaffInput || null}
                  data={employeeData
                    ?.filter((emp) => emp?.status !== "resigned")
                    .map((emp) => ({ value: emp?.id, label: emp?.name }))}
                  onChange={(value) => {
                    setWarehouseStaffInput(value);
                    handleDataChange("warehouseStaffId", value);
                  }}
                />
              )}
            </div>
            {fieldWrapper(
              t("warehouse.remarks") || "備註",
              IconFileText,
              <Input
                leftSection={<IconFileText size={16} />}
                value={remarks}
                onChange={(e) => handleDataChange("remarks", e.target.value)}
              />
            )}
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            variant="default"
            fullWidth
            radius="md"
            onClick={() => navigate(`/hrMainPage/inventoryCreated`)}
          >
            {t("warehouse.cancel") || "取消"}
          </Button>

          <Button
            variant="filled"
            color="gray"
            fullWidth
            radius="md"
            onClick={() =>
              handleSubmit(
                editInventoryCreatedData,
                tempProductList,
                deleteProductList
              )
            }
          >
            {t("quotation.submitEdit") || "送出編輯"}
          </Button>
        </div>
      </div>
    </div>
  );
}
export default EditInventoryCreatedListPage;
